"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import { preloadScreen } from "@/components/gallery/screen-image";
import { CYCLE_FADE_MS, CYCLE_STEP_MS, canCycle } from "@/lib/firstparty/hover-cycle";

/**
 * Steps a card through `frames` while a mouse rests on it: each screen shows for CYCLE_STEP_MS,
 * and the next one is loaded and decoded before it takes over. Leaving goes back to the first.
 * `prev` is the screen handing over, kept for CYCLE_FADE_MS so it can blur away under the new one.
 */
export function useHoverCycle(frames: string[], sizes: string) {
  const [shown, setShown] = useState<{ index: number; prev: number | null }>({ index: 0, prev: null });
  const [hovering, setHovering] = useState(false);
  const index = useRef(0);
  const run = useRef(0);
  const stepTimer = useRef<number | undefined>(undefined);
  const fadeTimer = useRef<number | undefined>(undefined);
  const framesRef = useRef(frames);
  useEffect(() => {
    framesRef.current = frames;
  }, [frames]);

  const show = useCallback((next: number) => {
    const from = index.current;
    if (next === from) return;
    index.current = next;
    setShown({ index: next, prev: from });
    window.clearTimeout(fadeTimer.current);
    fadeTimer.current = window.setTimeout(() => setShown({ index: next, prev: null }), CYCLE_FADE_MS);
  }, []);

  const step = useCallback(
    (gen: number) => {
      stepTimer.current = window.setTimeout(async () => {
        const list = framesRef.current;
        // A screen whose copy will not load is passed over, never shown blank.
        for (let tries = 1; tries < list.length; tries++) {
          const next = (index.current + tries) % list.length;
          const ok = await preloadScreen(list[next], sizes);
          if (gen !== run.current) return;
          if (!ok) continue;
          show(next);
          void preloadScreen(list[(next + 1) % list.length], sizes);
          break;
        }
        step(gen);
      }, CYCLE_STEP_MS);
    },
    [show, sizes],
  );

  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== "mouse" || framesRef.current.length < 2 || !canCycle(window.matchMedia?.bind(window))) return;
    const gen = ++run.current;
    window.clearTimeout(stepTimer.current);
    setHovering(true);
    void preloadScreen(framesRef.current[(index.current + 1) % framesRef.current.length], sizes);
    step(gen);
  };
  const onPointerLeave = () => {
    run.current++;
    window.clearTimeout(stepTimer.current);
    setHovering(false);
    show(0);
  };

  useEffect(() => {
    return () => {
      run.current++;
      window.clearTimeout(stepTimer.current);
      window.clearTimeout(fadeTimer.current);
    };
  }, []);

  return { index: shown.index, prev: shown.prev, hovering, onPointerEnter, onPointerLeave };
}
